import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ContentChild,
  ContentChildren,
  Directive, ElementRef, EventEmitter, HostListener,
  Input, OnDestroy, OnInit, Output,
  QueryList,
  TemplateRef, ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {CloFilterTableService} from './clo-filter-table.service';
import {NgbModal} from '../modal/modal.module';

import {Observable, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, filter} from 'rxjs/operators';
import {FormControl} from '@angular/forms';
import {CloMobileCard} from './table';

interface Filter {
  value: any;
  title: string;
  attribute: string;
}

/**
 *  Filter Options
 */
@Directive({
  selector: 'clo-filter-option'
})
export class CloFilterOption implements OnInit {

  /**
   *  If not specified will default to value
   */
  @Input() title: string;
  @Input() value: string | number;

  @Input() applied = false;

  constructor() {}

  ngOnInit(): void {
    this.title = this.title || this.value.toString();
  }
}
/**
 *  Filter Groups to organize filters
 */
@Directive({
  selector: 'clo-filter-group'
})
export class CloFilterGroup {

  @Input() title: string;
  @Input() applyAttribute: string;

  @ContentChildren(CloFilterOption, {descendants: false}) options: QueryList<CloFilterOption>;
  constructor() {}

}
/**
 *  Filter Block
 */
@Directive({
  selector: 'clo-filters'
})
export class CloFilters {

  @ContentChildren(CloFilterGroup, {descendants: false}) filterGroups: QueryList<CloFilterGroup>;
}
/**
 *  Export Option
 */
@Directive({
  selector: 'clo-export-option'
})
export class CloExportOption {

  /**
   * Emits when option is selected
   */
  @Output() selected = new EventEmitter();

  /**
   * The title to be displayed in the dropdown
   */
  @Input() title: string;
  constructor() {}

}
/**
 * The component where search, filters and export options live.  It can be placed anywhere on the view to interact with the table.
 */
@Component({
  selector: 'clo-table-block',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [DecimalPipe],
  host: {
  '(window:resize)': 'onResize($event)'
},
  templateUrl: 'table-block.html',
  styleUrls: ['./table.scss']
})

export class CloTableBlock implements OnInit, AfterViewInit, OnDestroy {

  public searchFilter: string;

  /**
   * This is required to work.  You must have the table id in this format
   * '#TableId'
   */
  @Input() tableId: string;

  /**
   * Allows the search function
   */
  @Input() searchable = true;

  @Output() filterApplied = new EventEmitter();

  /**
   * This returns an object indicating which modal was toggled
   *    object{
   *      modalName: string
   *      toggleStatus: string = opened/closed
   *    }
   */
  @Output() modalToggled = new EventEmitter();

  private appliedFilters: Filter[];
  private searchableData = [];

  @ContentChild(CloFilters,  {static: false}) filters: CloFilters;
  @ContentChildren(CloExportOption, {descendants: false}) exportOptions: QueryList<CloExportOption>;
  @ViewChild('modalSearchInput', {static: false}) modalSearchInput: ElementRef;
  @ViewChild('searchInput', {static: false}) searchInput: ElementRef;

  private readonly filterSubscription: Subscription;
  searchField = new FormControl('');
  mobileCards: QueryList<CloMobileCard>;
  public tableTypeName: string;
  public visibleCards: number;
  private modal;

  open(content) {
    this.modal = this.modalService.open(content, {windowClass: 'filterModal md-full-modal '});
    this.emitModalToggled(content, 'opened');

    this.modal.result.then(
      () => { this.emitModalToggled(content, 'closed'); },  // close button
      () => { this.emitModalToggled(content, 'closed'); }   // esc, backdrop clicked
    );
  }

  private emitModalToggled(elementRef, toggleStatus) {
    if (elementRef._def) {
      return this.modalToggled.emit({modalName: Object.keys(elementRef._def.references)[0], toggleStatus: toggleStatus});
    }
  }

  search = (text$: Observable<string>) => text$.pipe(
    debounceTime(100),
    distinctUntilChanged(),
    filter(term => term.length >= 3),
    map(term => {
      const escapedString = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return this.searchableData.filter(f => {
        return new RegExp(escapedString, 'mi').test(f.toString());
      }).slice(0, 15);
    })
  )

  ngOnDestroy() {
    this.appliedFilters = [];
    this.ref.detach();
    this.filterTableService.clearFilters(this.tableId);
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

  }

  constructor(private elementRef: ElementRef, private filterTableService: CloFilterTableService,
              private ref: ChangeDetectorRef, private modalService: NgbModal) {
    this.appliedFilters = this.appliedFilters || [];
    this.exportOptions = this.exportOptions || new QueryList<CloExportOption>();
    this.filterSubscription = this.filterTableService.appliedFilters.subscribe( filters => {
      this.appliedFilters = filters[this.tableId];
      if (this.filters !== undefined && this.filters && this.filters.filterGroups !== undefined) {
        this.filters.filterGroups.forEach( group => {
          group.options.forEach( f => {
            f.applied = false;
            this.appliedFilters.forEach( ap => {
              if (ap.value === f.value) {
                f.applied = true;
              }
            });
          });
        });
      }

    });
    if (this.searchable) {
      this.filterTableService.searchableData.subscribe( data => {
        // console.log(data);
        this.searchableData = data[this.tableId];
      });
    }

  }

  ngAfterViewInit(): void {

    if (this.filters !== undefined && this.filters && this.filters.filterGroups !== undefined) {
      this.filters.filterGroups.forEach(group => {
        group.options.forEach( f => {
          if (f.applied) {
            this.applyFilter(undefined, f.value, group.applyAttribute, f.title);
          }
        });
      });
    }

  }

  ngOnInit(): void {
    this.filterTableService.observableMobileCards.subscribe( mobileCards => {
      if (mobileCards[this.tableId]) {
        this.mobileCards = mobileCards[this.tableId];
        this.tableTypeName = this.filterTableService.tableTypeName;
        this.visibleCards = this.mobileCards.filter( i => !i.hidden).length;
        this.ref.detectChanges();
      }
    });
  }

  @HostListener('window:touchstart', ['$event'])
  unselectInputInIos( event ) {
    const modalSearch = (this.modalSearchInput) ? this.modalSearchInput.nativeElement : undefined;
    const searchInput = (this.searchInput) ? this.searchInput.nativeElement : undefined;
    if ( document.activeElement instanceof HTMLElement
      && this.isiOS()
      && (document.activeElement === searchInput || document.activeElement === modalSearch)
      && event.target !== document.activeElement
    ) {
      // document.activeElement.blur();
      this.searchInput.nativeElement.blur();
      this.modalSearchInput.nativeElement.blur();
    }
  }

  isiOS() { return navigator.userAgent.match(/ipad|ipod|iphone/i); }

  changeSearchFilter(e) {
    // this.applySearchFilter(e.target.value);
    this.filterTableService.updateSearchTerm(e.target.value, this.tableId);
  }

  applySearchFilter(value) {
    this.filterTableService.updateSearchTerm(value.item, this.tableId);
  }

  applyFilter(event, value, attribute, title = undefined) {
    const newFilter = {value: value, title: title || value, attribute: attribute} ;

    this.filterTableService.addFilter(this.tableId, newFilter);
    this.filterApplied.emit(this.appliedFilters);
    this.ref.detectChanges();
  }

  clearInput(e) {
    const nearnessToRight = e.target.getBoundingClientRect().right - e.clientX;
    if ( nearnessToRight < 43 ) {
      this.searchField.setValue('');
      this.filterTableService.updateSearchTerm('', this.tableId);
    }
    if (this.modalSearchInput) {
      this.modalSearchInput.nativeElement.value = '';
    }
  }

  onResize(event) {
    // console.log(event.target.innerHTML > );
    const windowWidth = event.target.innerWidth;
    if (windowWidth > 990) {
      if (this.modal) {
        this.modal.close();
        this.clearInput(event);
      }

    }
  }
}
